import React, { useMemo } from "react";
import { MONTH_NAMES } from "../../lib/format";

export const TimelineView = ({ entries, onOpenDetail }) => {
  const sorted = useMemo(() => {
    return [...entries]
      .filter((e) => e.year_established)
      .sort((a, b) => (a.year_established || 0) - (b.year_established || 0));
  }, [entries]);

  // Group by decade buckets.
  const decades = useMemo(() => {
    const map = new Map();
    for (const e of sorted) {
      const decade = Math.floor((e.year_established || 0) / 10) * 10;
      if (!map.has(decade)) map.set(decade, []);
      map.get(decade).push(e);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [sorted]);

  if (sorted.length === 0) {
    return (
      <div style={{ padding: "3rem 1rem", textAlign: "center", border: "1px dashed hsl(var(--line))" }}>
        <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic" }}>
          No dated entries match these filters yet.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="timeline-view" style={{ position: "relative" }}>
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 28,
          top: 0,
          bottom: 0,
          width: 1,
          background: "hsl(var(--line))",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}>
        {decades.map(([decade, items]) => (
          <section key={decade} aria-labelledby={`decade-${decade}`}>
            <header style={{ position: "relative", marginBottom: "1rem", paddingLeft: 64 }}>
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: 22,
                  top: 6,
                  width: 14, height: 14,
                  borderRadius: 999,
                  background: "hsl(var(--accent))",
                  border: "3px solid hsl(var(--bg))",
                  boxShadow: "0 0 0 1px hsl(var(--accent))",
                }}
              />
              <h3 id={`decade-${decade}`} className="archival-figure" style={{
                fontFamily: "var(--font-display)",
                fontSize: "1.65rem",
                fontWeight: 500,
                margin: 0,
              }}>
                {decade}s
              </h3>
              <span style={{ fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "hsl(var(--muted))" }}>
                {items.length} {items.length === 1 ? "entry" : "entries"}
              </span>
            </header>
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
              {items.map((e) => (
                <li key={e.id} style={{ position: "relative", paddingLeft: 64 }}>
                  <span aria-hidden style={{
                    position: "absolute",
                    left: 25, top: 14,
                    width: 7, height: 7,
                    borderRadius: 999,
                    background: "hsl(var(--bg))",
                    border: "1px solid hsl(var(--accent) / 0.6)",
                  }} />
                  <button
                    type="button"
                    onClick={() => onOpenDetail?.(e)}
                    data-testid="timeline-entry"
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: "hsl(var(--bg-elev))",
                      border: "1px solid hsl(var(--line))",
                      padding: "0.95rem 1.15rem",
                      cursor: "pointer",
                      transition: "border-color 160ms ease, transform 160ms ease",
                      display: "flex",
                      gap: 16,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.borderColor = "hsl(var(--accent))"; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.borderColor = "hsl(var(--line))"; }}
                  >
                    <span
                      className="archival-figure"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "1.4rem",
                        fontWeight: 500,
                        color: "hsl(var(--accent))",
                        flex: "0 0 auto",
                        minWidth: 64,
                      }}
                    >
                      {e.year_established}
                    </span>
                    <div style={{ flex: "1 1 240px" }}>
                      <div style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "1.05rem",
                        fontWeight: 500,
                        color: "hsl(var(--ink))",
                        marginBottom: 4,
                      }}>
                        {e.title}
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "hsl(var(--muted))" }}>
                        {e.month ? MONTH_NAMES[e.month] : e.date_label}
                        {e.day ? ` ${e.day}` : ""} · {e.recognition_status}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
};
